import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export default function CourseDetails() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900"
    >
      <SiteHeader />

      <div className="mx-auto max-w-4xl px-4 py-12 sm:py-16 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 lg:p-12"
        >
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-4">
              Certificate Course in
              <span className="block bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-rose-500">
                Printed Circuit Board Manufacturing
              </span>
            </h1>
            <div className="inline-flex items-center rounded-full bg-orange-100 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-orange-700">
              12 Weeks Duration
            </div>
          </div>

          <div className="space-y-8">
            {/* About the Course */}
            <section>
              <h2 className="text-xl sm:text-2xl font-bold mb-4 text-slate-900">About the Course</h2>
              <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                The Certificate Course in Printed Circuit Board Manufacturing imparts practical skills used in different processes of PCB manufacturing.
              </p>
            </section>

            {/* What it covers */}
            <section>
              <h2 className="text-xl sm:text-2xl font-bold mb-4 text-slate-900">What the Course Covers</h2>
              <ul className="space-y-2">
                {[
                  "Introduction to different types of PCBs",
                  "Familiarization of electronic components",
                  "Process of PCB manufacturing for Single-sided, Double-sided, and Multilayer PCBs",
                  "Inspection and testing of PCBs",
                  "Raw materials used in PCBs",
                  "Application of IPC standards",
                  "Safety and environmental concerns",
                  "Machine maintenance"
                ].map((item, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-orange-500 flex-shrink-0" />
                    <span className="text-slate-700 text-sm sm:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Course Structure */}
            <section>
              <h2 className="text-xl sm:text-2xl font-bold mb-4 text-slate-900">Course Structure</h2>
              <p className="text-slate-600 mb-4 text-sm sm:text-base">The course is divided into three parts:</p>
              <div className="grid gap-3 sm:gap-4 md:grid-cols-3">
                <div className="bg-slate-50 rounded-xl p-3 sm:p-4 text-center">
                  <div className="text-base sm:text-lg font-semibold text-slate-900">Theory</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 sm:p-4 text-center">
                  <div className="text-base sm:text-lg font-semibold text-slate-900">Practical</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 sm:p-4 text-center">
                  <div className="text-base sm:text-lg font-semibold text-slate-900">Industry Internship</div>
                </div>
              </div>
            </section>

            {/* Job Opportunities */}
            <section>
              <h2 className="text-xl sm:text-2xl font-bold mb-4 text-slate-900">Job Opportunities</h2>
              <p className="text-slate-600 mb-4 text-sm sm:text-base">Candidates can get jobs in PCB manufacturing facilities as:</p>
              <div className="grid gap-3 md:grid-cols-2">
                {[
                  "CNC Drilling & Routing Operator",
                  "Circuit Imaging Operator",
                  "Plating & Etching Operator",
                  "Solder Mask Printing Operator",
                  "HAL Machine Operator",
                  "Flying Probe Machine Operator",
                  "Production Planning and Control Technician",
                  "Quality Control Technician"
                ].map((job, index) => (
                  <div key={index} className="flex items-center gap-3 bg-orange-50 rounded-lg p-3">
                    <span className="text-orange-600">💼</span>
                    <span className="text-slate-700 font-medium text-sm sm:text-base">{job}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Key Competencies */}
            <section>
              <h2 className="text-xl sm:text-2xl font-bold mb-4 text-slate-900">Key Competencies Developed</h2>
              <ul className="space-y-3">
                {[
                  "Understanding complete PCB manufacturing process steps",
                  "Knowledge of raw materials and chemicals used in PCB manufacturing",
                  "Familiarity with test equipment, tools, and machines used in PCB production",
                  "Comprehension of critical process parameters and acceptability criteria",
                  "Understanding and applying IPC standards",
                  "Development of soft skills"
                ].map((competency, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
                    <span className="text-slate-700 text-sm sm:text-base">{competency}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Syllabus */}
            <section>
              <h2 className="text-xl sm:text-2xl font-bold mb-6 text-slate-900">Syllabus</h2>
              <div className="space-y-4">
                {[
                  {
                    module: "Module 1",
                    title: "Materials & Tools for PCB Manufacturing",
                    color: "from-blue-500 to-cyan-500"
                  },
                  {
                    module: "Module 2",
                    title: "Basic Processes for PCB Manufacturing",
                    color: "from-green-500 to-teal-500"
                  },
                  {
                    module: "Module 3",
                    title: "Processes for Integrated PCB Manufacturing",
                    color: "from-purple-500 to-indigo-500"
                  },
                  {
                    module: "Module 4",
                    title: "Safety and Environmental Issues",
                    color: "from-red-500 to-pink-500"
                  },
                  {
                    module: "Module 5",
                    title: "Soft Skills",
                    color: "from-yellow-500 to-orange-500"
                  }
                ].map((item, index) => (
                  <div key={index} className={`bg-gradient-to-r ${item.color} rounded-xl p-3 sm:p-4 text-white`}>
                    <div className="font-bold text-base sm:text-lg">{item.module}:</div>
                    <div className="font-semibold text-sm sm:text-base">{item.title}</div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Call to Action */}
          <div className="mt-8 sm:mt-12 text-center">
            <div className="bg-gradient-to-r from-orange-500 to-rose-500 rounded-2xl p-6 sm:p-8 text-white">
              <h3 className="text-xl sm:text-2xl font-bold mb-4">Ready to Start Your PCB Manufacturing Career?</h3>
              <p className="mb-4 sm:mb-6 opacity-90 text-sm sm:text-base">Join our comprehensive 12-week program and gain hands-on experience in PCB manufacturing.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/apply"
                  className="inline-flex items-center justify-center rounded-xl px-4 sm:px-6 py-3 font-semibold bg-white text-orange-600 hover:bg-slate-50 transition text-sm sm:text-base"
                >
                  Apply Now
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center rounded-xl px-4 sm:px-6 py-3 font-semibold bg-white/20 text-white hover:bg-white/30 transition text-sm sm:text-base"
                >
                  Back to Programs
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <SiteFooter />
    </motion.div>
  );
}

function Logo({ className = "h-9 w-auto" }) {
  return (
    <Link to="/" className="flex items-center gap-2">
      <img src="/logo.png" alt="NCAI" className={className} />
      <span className="text-xl font-extrabold tracking-tight">
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600">NCAI</span>
      </span>
    </Link>
  );
}

function SiteHeader() {
  return (
    <header className="sticky top-0 z-50 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-slate-200/60">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline text-sm font-semibold text-slate-700">NeuraCore AI Institute</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
            <Link to="/#programs" className="hover:text-indigo-600">Programs</Link>
            <Link to="/workshops" className="hover:text-indigo-600">Workshops</Link>
            <Link to="/#flex" className="hover:text-indigo-600">Flexible Learning</Link>
            <Link to="/#outcomes" className="hover:text-indigo-600">Outcomes</Link>
            <Link to="/#faq" className="hover:text-indigo-600">FAQ</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/apply"
              className="rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 shadow-md hover:shadow-lg transition"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-slate-200 bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-8 lg:grid-cols-4 md:grid-cols-2">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <Logo />
              <span className="text-sm font-semibold text-slate-700">NeuraCore AI Institute</span>
            </Link>
            <p className="text-sm text-slate-600 mb-4">
              Empowering careers in Digital Marketing, AI & Machine Learning, and Hardware Design & Manufacturing.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/#programs" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Programs</Link></li>
              <li><Link to="/workshops" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Workshops</Link></li>
              <li><Link to="/#flex" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Flexible Learning</Link></li>
              <li><Link to="/#outcomes" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Outcomes</Link></li>
              <li><Link to="/#faq" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">FAQ</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Contact Us</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:info@ncai.edu" className="hover:text-indigo-600 transition-colors">info@ncai.edu</a>
              </li>
              <li className="flex items-center gap-2">
                <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href="tel:+1234567890" className="hover:text-indigo-600 transition-colors">+1 (234) 567-8900</a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Stay Updated</h3>
            <p className="text-sm text-slate-600 mb-4">Subscribe to our newsletter for the latest updates and insights.</p>
            <form className="space-y-2">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button
                type="submit"
                className="w-full px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 rounded-lg hover:shadow-md transition"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-slate-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">© {new Date().getFullYear()} NeuraCore AI Institute (NCAI). All rights reserved.</p>
            <div className="flex gap-6 text-sm text-slate-500">
              <a href="#" className="hover:text-indigo-600 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-indigo-600 transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}